import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * A jump menu for a page carrying several Product Picker 2 sections.
 *
 * **It reads the page rather than being told about it.** A section cannot reach another section's
 * settings in Liquid, so the thread headings cannot be known server-side. The alternative was a
 * block per link with every heading typed a second time, which goes stale the first time somebody
 * renames a thread. Reading the rendered headings means the menu cannot disagree with what it
 * points at, and a thread added later shows up without anyone opening this section.
 *
 * The cost is that the menu does not exist without script. Accepted: it sits over content that is
 * all still present and still reachable by scrolling, so the failure is a lost convenience rather
 * than lost content. The markup ships `hidden` and is revealed only once there are links in it.
 *
 * Anchors are the `shopify-section-…` ids Shopify already writes onto every section wrapper, so
 * the threads need nothing added to them.
 */

const THREAD_SELECTOR = '.product-picker-2-wrapper';
const HEADING_SELECTOR = '.product-picker-2__heading';

/**
 * @typedef {Object} NavRefs
 * @property {HTMLElement} [sentinel]
 * @property {HTMLElement} [sticky]
 * @property {HTMLElement} [bar]
 * @property {HTMLUListElement} [list]
 */

/**
 * @extends {Component<NavRefs>}
 */
class ProductPickerNav extends Component {
  requiredRefs = ['list', 'bar'];

  /** @type {IntersectionObserver | null} */
  #spy = null;

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {Map<string, HTMLAnchorElement>} */
  #links = new Map();

  /** @type {Map<Element, number>} */
  #ratios = new Map();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const threads = this.#collectThreads();
    if (!threads.length) return;

    this.#build(threads);
    this.#observeThreads(threads.map(({ element }) => element));
    this.#observeSticky();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#spy?.disconnect();
    this.#spy = null;
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    document.documentElement.style.removeProperty('--bk-subnav-height');
    this.#links.clear();
    this.#ratios.clear();
  }

  /**
   * Every thread on the page that has both an id to point at and a heading to point with. A thread
   * whose heading the merchant left empty is skipped rather than given a blank chip: there would be
   * nothing to click and nothing to read.
   *
   * @returns {{ element: HTMLElement, id: string, label: string }[]}
   */
  #collectThreads() {
    /** @type {{ element: HTMLElement, id: string, label: string }[]} */
    const threads = [];

    for (const element of document.querySelectorAll(THREAD_SELECTOR)) {
      if (!(element instanceof HTMLElement) || !element.id) continue;

      const heading = element.querySelector(HEADING_SELECTOR);
      const label = heading?.textContent?.trim();
      if (!label) continue;

      threads.push({ element, id: element.id, label });
    }

    return threads;
  }

  /**
   * @param {{ element: HTMLElement, id: string, label: string }[]} threads
   * @returns {void}
   */
  #build(threads) {
    const { list, bar } = this.refs;
    if (!list || !bar) return;

    const fragment = document.createDocumentFragment();

    for (const { id, label } of threads) {
      const item = document.createElement('li');
      const link = document.createElement('a');

      link.className = 'product-picker-nav__link subnav-pill__link';
      link.href = `#${id}`;
      link.textContent = label;
      link.dataset.target = id;

      item.append(link);
      fragment.append(item);
      this.#links.set(id, link);
    }

    list.append(fragment);
    bar.hidden = false;

    // Published on the document, not on this element, because the threads need it too: a thread's
    // `scroll-margin-block-start` has to clear the bar or jumping to one parks its heading behind
    // the bar that sent you there. Custom properties inherit, so the spacer rule on this element
    // reads the same value.
    this.#publishHeight();
    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(bar);

    // One delegated listener rather than `on:click` per link, because the links are built here and
    // an attribute added from script is not picked up by the declarative binding.
    list.addEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-target]') : null;
    if (!(target instanceof HTMLAnchorElement)) return;

    // Anything that is not a plain left click stays the browser's business, so cmd-click still
    // opens the anchor in a new tab. Same bail-out as the collection filter chips.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const id = target.dataset.target;
    const thread = id ? document.getElementById(id) : null;
    if (!thread) return;

    event.preventDefault();

    thread.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

    // `replaceState`, not `pushState`: a jump menu is a way of moving around one page, and five
    // clicks should not bury the page somebody arrived from under five history entries. The
    // collection filter draws the same line.
    history.replaceState(history.state, '', `#${id}`);

    this.#setActive(id);
  };

  /**
   * Scroll spy.
   *
   * Tracks the visible ratio of every thread and marks the largest, rather than marking whichever
   * one most recently crossed an edge. A thread can be taller than the viewport — four products at
   * three across, plus an intro — so an "entered the top" rule leaves the tall one marked long
   * after it has gone, and a plain `isIntersecting` rule flickers wherever two threads share the
   * screen.
   *
   * @param {HTMLElement[]} elements
   * @returns {void}
   */
  #observeThreads(elements) {
    this.#spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#ratios.set(entry.target, entry.intersectionRatio);

        let best = null;
        let bestRatio = 0;

        for (const [element, ratio] of this.#ratios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = element;
          }
        }

        // Nothing on screen at all: keep the last mark rather than clearing it, so scrolling
        // through a gap between threads does not blank the bar.
        if (best instanceof HTMLElement && best.id) this.#setActive(best.id);
      },
      {
        // Mandatory, not tidiness: at 990px and up the scroll container is `.page-wrapper` rather
        // than the document, and an observer left on the default root silently never fires there.
        root: getIntersectionRoot(),
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const element of elements) this.#spy.observe(element);
  }

  /**
   * `position: sticky` offers no way to ask whether it is currently stuck, so a 1px sentinel above
   * the bar is watched instead. It is also out of view while the section is still below the fold,
   * which is why `isIntersecting` alone is not enough: it counts as stuck only once the sentinel
   * has passed above the top of the scrollport.
   *
   * @returns {void}
   */
  #observeSticky() {
    const { sentinel } = this.refs;
    if (!sentinel || !this.hasAttribute('data-sticky')) return;

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const top = entry.rootBounds?.top ?? 0;
        this.#setStuck(!entry.isIntersecting && entry.boundingClientRect.top < top);
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );

    this.#stickyObserver.observe(sentinel);
  }

  /**
   * Two heights.
   *
   * - `--subnav-flow-height`, on this element: the bar as it sits in the page, for the spacer that holds
   *   its place once it leaves the flow. **Measured only while not stuck.** The stuck pill is
   *   shorter (no heading, less padding), and re-measuring it shrank the spacer, which moved the
   *   page up, which unstuck the bar, which grew the spacer back: a loop that read as the page
   *   juddering on a slow scroll past the menu, with the header popping in and out on top.
   * - `--bk-subnav-height`, on the document element: whatever it is now, for thread `scroll-margin` and the item
   *   panel's room. Neither of those affects where the sentinel sits, so it cannot feed back.
   *
   * @returns {void}
   */
  #publishHeight() {
    const height = this.refs.sticky?.getBoundingClientRect().height;
    if (!height) return;

    const px = `${Math.round(height)}px`;
    if (!this.hasAttribute('data-stuck')) this.style.setProperty('--subnav-flow-height', px);
    document.documentElement.style.setProperty('--bk-subnav-height', px);
  }

  /**
   * @param {boolean} stuck
   * @returns {void}
   */
  #setStuck(stuck) {
    if (stuck === this.hasAttribute('data-stuck')) return;

    this.toggleAttribute('data-stuck', stuck);
  }

  /**
   * Mirrors the collection filter bar: an attribute plus `aria-current`, never a class.
   *
   * @param {string} id
   * @returns {void}
   */
  #setActive(id) {
    for (const [key, link] of this.#links) {
      const active = key === id;
      if (link.hasAttribute('data-active') === active) continue;

      link.toggleAttribute('data-active', active);

      if (active) {
        link.setAttribute('aria-current', 'true');
        this.#revealLink(link);
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Bring the active chip into view *within its own row*, on a phone where the row scrolls
   * sideways and the current thread can be off the end of it.
   *
   * Deliberately not `scrollIntoView`. That walks up and scrolls every scrollable ancestor, and
   * this bar is `position: sticky` inside the page's scroll container — so asking for the chip put
   * the container back where the bar sits in flow, every time the spy fired. The page could not be
   * scrolled past the bar at all: it snapped back within a frame, which reads as a broken page
   * rather than as a bug in a menu. Touch this element's own `scrollLeft` and nothing else.
   *
   * @param {HTMLAnchorElement} link
   * @returns {void}
   */
  #revealLink(link) {
    const { bar } = this.refs;
    if (!bar || bar.scrollWidth <= bar.clientWidth) return;

    const linkBox = link.getBoundingClientRect();
    const barBox = bar.getBoundingClientRect();
    const delta = linkBox.left - barBox.left - (barBox.width - linkBox.width) / 2;

    bar.scrollBy({ left: delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('product-picker-nav')) {
  customElements.define('product-picker-nav', ProductPickerNav);
}
